import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Pencil,
  Plus,
  Trash2,
  X,
  Sparkles
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { SearchableSelect } from "../components/ui/SearchableSelect";

import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import useToast from "../hooks/useToast";
import { getCategories } from "../services/categoryService";
import { getProfessors } from "../services/professorService";
import {
  createPublication,
  deletePublication,
  getPublications,
  updatePublication
} from "../services/publicationService";

const publicationSchema = z.object({
  department: z.string().min(1, "Sub department is required"),
  name: z.string().trim().min(1, "Faculty name is required"),
  publication: z.string().trim().min(1, "Publication description is required"),
  published_on: z.string().trim().min(1, "Indexing system is required")
});

const defaultValues = {
  department: "",
  name: "",
  publication: "",
  published_on: ""
};

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Publication() {
  const [publications, setPublications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(publicationSchema)
  });

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({ ...current, page: targetPage, limit: targetLimit }));
      return;
    }
    loadPublications(targetPage, targetLimit);
  };

  async function loadInitialData() {
    const categoryRes = await getCategories({ limit: 100 });
    if (categoryRes?.success) {
      setCategories(categoryRes.data.categories || []);
    }
    const professorRes = await getProfessors({ limit: 1000 });
    if (professorRes?.success) {
      setProfessors(professorRes.data.professors || []);
    }
  }

  async function loadPublications(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);
    const response = await getPublications({ page: pageArg, limit: limitArg });
    if (response?.success) {
      setPublications(response.data.publications || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load publications");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadInitialData();
    loadPublications(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleEdit = (publication) => {
    setEditingId(publication.id);
    reset({
      department: publication.department ? String(publication.department) : "",
      name: publication.name || "",
      publication: publication.publication || "",
      published_on: publication.published_on || ""
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    const response = await deletePublication(pendingDelete.id);
    setIsDeleting(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete publication");
      return;
    }

    const nextPage = publications.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Publication deleted successfully");
    setPendingDelete(null);
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const response = editingId
      ? await updatePublication(editingId, values)
      : await createPublication(values);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save publication");
      return;
    }

    showSuccessToast(response.message || "Publication saved successfully");
    closeModal();
    if (editingId) refreshListing(pagination.page, pagination.limit);
    else refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="NMC Info"
        title="Publication Details"
        description="Manage medical research publications and academic contributions by SPMCRC faculty."
        badge={`${pagination.total} Records`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Publications List</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">View records and manage entries with paginated control.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" /> Add Publication
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border p-6 text-sm text-panel-muted">Loading publications...</div>
          ) : publications.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Sub department</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Faculty Name</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Publication (Vancouver Style)</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Indexing System</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {publications.map((item) => (
                        <tr key={item.id} className="border-t border-slate-100 align-top">
                          <td className="px-5 py-4 text-sm text-panel-ink">
                            <span className="inline-block px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-bold uppercase tracking-wide">
                              {item.department_name || "N/A"}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{item.name}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted max-w-xs break-words">{item.publication}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{item.published_on}</td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(item)}>
                                <Pencil className="mr-1 h-4 w-4" /> Edit
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted" onClick={() => setPendingDelete(item)}>
                                <Trash2 className="mr-1 h-4 w-4" /> Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to <span className="font-semibold text-panel-ink">{endItem}</span> of <span className="font-semibold text-panel-ink">{pagination.total}</span> publications
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => refreshListing(pagination.page - 1)}>Prev</Button>
                  {pageNumbers.map((p, i) => (
                    p === "..." ? <span key={i} className="px-1 text-sm">...</span> : 
                    <Button key={i} type="button" size="sm" variant={pagination.page === p ? "default" : "outline"} onClick={() => refreshListing(p)}>{p}</Button>
                  ))}
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => refreshListing(pagination.page + 1)}>Next</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 p-8 text-center">
              <BookOpen className="mx-auto h-10 w-10 text-primary/20 mb-4" />
              <p className="text-lg font-semibold">No publications added yet.</p>
              <Button type="button" className="mt-4" onClick={openCreateModal}><Plus className="mr-2 h-4 w-4" /> Add Your First Publication</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 overflow-y-auto">
          <div className="max-h-[90dvh] w-full max-w-xl overflow-y-auto rounded-[32px] border bg-white shadow-xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h2 className="text-2xl font-bold">{editingId ? "Edit Publication" : "Add Publication"}</h2>
              <button type="button" onClick={closeModal}><X /></button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2 text-panel-ink">
                <FieldLabel>Sub department *</FieldLabel>
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={categories}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Search and select sub department..."
                    />
                  )}
                />
                {errors.department && <FieldError>{errors.department.message}</FieldError>}
              </div>

              <div className="space-y-2 text-panel-ink">
                <FieldLabel>Faculty Name *</FieldLabel>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={professors.map(p => ({ id: p.name, name: `${p.name} (${p.designation})` }))}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Search and select faculty member..."
                    />
                  )}
                />
                {errors.name && <FieldError>{errors.name.message}</FieldError>}
              </div>

              <div className="space-y-2">
                <FieldLabel>Publication in vancouver referencing style *</FieldLabel>
                <Textarea placeholder="E.g. Smith J, Doe J. Article Title. Journal Name. 2026;10(2):100-105." {...register("publication")} rows={4} />
                {errors.publication && <FieldError>{errors.publication.message}</FieldError>}
              </div>

              <div className="space-y-2">
                <FieldLabel>Indexing system *</FieldLabel>
                <Input placeholder="E.g. PubMed, Scopus, Index Copernicus" {...register("published_on")} />
                {errors.published_on && <FieldError>{errors.published_on.message}</FieldError>}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : editingId ? "Update Publication" : "Save Publication"}
                </Button>
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border bg-white p-6 shadow-xl text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 mx-auto mb-4">
              <Trash2 className="h-7 w-7" />
            </div>
            <h2 className="text-2xl font-bold">Delete Publication?</h2>
            <p className="mt-3 text-panel-muted">Deleting publication for <span className="font-bold text-panel-ink">{pendingDelete.name}</span> is permanent.</p>
            <div className="mt-8 flex gap-4">
              <Button type="button" variant="destructive" className="flex-1" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Confirm Delete"}
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setPendingDelete(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
